import { ReactElement, useEffect, useRef, useState } from 'react'

import { useHandler, useOnDialogBlur, useUniqId } from '@surglogs-app/hooks'

import { usePromptsStore } from './PromptsContext'

export interface PromptFunctionProps<TAnswer> {
  answer(value: TAnswer): void
}

export interface PromptFunction<TAnswer> {
  (
    prompt: (props: PromptFunctionProps<TAnswer>) => ReactElement | null,
  ): Promise<TAnswer>
}

export type UsePromptOpts = Partial<{
  autoClose: boolean
  disableCloseOnBlur: boolean
}>

export function usePrompt<TAnswer>({
  autoClose = true,
  disableCloseOnBlur = false,
}: UsePromptOpts = {}) {
  const id = useUniqId()
  const store = usePromptsStore()
  const [prompting, setPrompting] = useState(false)
  // the same prompt can be awaited by more than one caller (a before leave
  // guard and an onClose handler race for the same dialog), so a single answer
  // has to resolve every pending promise
  const resolversRef = useRef<((value: TAnswer) => void)[]>([])
  // autoClose and close on blur answer without a value, while PromptFunction
  // keeps its Promise<TAnswer> signature for callers that always answer
  const noAnswer = null as TAnswer

  const setResolvers = useHandler((resolvers: ((value: TAnswer) => void)[]) => {
    resolversRef.current = resolvers
    setPrompting(resolvers.length > 0)
  })

  const answer = useHandler((value: TAnswer) => {
    store.dispatch({ type: 'ANSWER', id })

    const resolvers = resolversRef.current

    setResolvers([])
    resolvers.forEach((resolve) => resolve(value))
  })

  const { register, cleanup } = useOnDialogBlur({
    id,
    callback: () => (disableCloseOnBlur ? undefined : answer(noAnswer)),
    autoRegister: false,
  })

  // useOnDialogBlur tracks one handler and one stack entry per hook, so
  // concurrent awaiters of the same prompt have to register once, and unmount
  // has to clean up even when the prompt is left pending
  useEffect(() => {
    if (!prompting) {
      return undefined
    }

    register()

    return cleanup
  }, [cleanup, prompting, register])

  useEffect(
    () => () => {
      if (autoClose === true) {
        answer(noAnswer)
      }
    },
    [answer, autoClose, noAnswer],
  )

  const prompt: PromptFunction<TAnswer> = useHandler(
    (renderer) =>
      new Promise((resolve, reject) => {
        const dropResolver = () =>
          setResolvers(resolversRef.current.filter((item) => item !== resolve))

        setResolvers([...resolversRef.current, resolve])

        try {
          const element = renderer({ answer })

          // a renderer may render nothing, then there is nothing to show and
          // nothing that could ever answer the prompt
          if (element === null) {
            dropResolver()
            resolve(noAnswer)

            return
          }

          store.dispatch({ type: 'PROMPT', id, prompt: element })
        } catch (e) {
          dropResolver()
          reject(e)
        }
      }),
  )

  return [prompt, prompting, id] as const
}
